import { Grid, Tooltip } from "@mui/material";
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridActionsCellItem, GridApiPro, GridColDef, GridColumnVisibilityModel, GridValueOptionsParams } from "@mui/x-data-grid-pro";
import { Ico_Delete } from "@sagecentral/core-ui";
import { IDashboardSettings } from "components/preferences";
import { TaskListTooltip } from "components/shared";
import { ChildFilterOperator, NumberFilterOperator, StatusFilterOperatorWithoutCancelled, TextFilterOperator } from "components/shared/filters";
import ContainsOnlyTextFilterOperator from "components/shared/filters/ContainsOnlyTextFilterOperator";
import ParentWorkItemListTooltip from "components/teams/grid/ParentWorkItemListTooltip";
import { SetPreferenceSignature } from "hooks/preferences";
import useGridColumns, { ColumnVisibilityOrOrderChangeHandler } from "hooks/shared/useGridColumns";
import { IAddRelatedWorkItemEntry, useWorkItemPermissions } from "hooks/work-items";
import { IListItem } from "models/common";
import { MutableRefObject, useMemo } from "react";
import { toPascalCase } from "utils/string-utils";


type UseAddRelatedColumnsResult = [
    GridColDef[],
    GridColumnVisibilityModel,
    ColumnVisibilityOrOrderChangeHandler
];

type UseAddRelatedColumnsSignature = (selection: boolean, apiRef?: MutableRefObject<GridApiPro | null>, settings?: IDashboardSettings, setSettings?: SetPreferenceSignature) => UseAddRelatedColumnsResult;

const useAddRelatedColumns: UseAddRelatedColumnsSignature = (selection, apiRef, settings, setSettings) => {
    
    const permissions = useWorkItemPermissions();
    
    const getRelationshipOptions = (params: GridValueOptionsParams<IAddRelatedWorkItemEntry>) => {
        const { row } = params;
        if (row === undefined) return [];

        return [
            {
                label: 'Child',
                value: true
            },
            {
                label: 'Parent',
                value: false
            }
        ]
    };

    const columnBase: GridColDef[] = [
        {
            ...GRID_CHECKBOX_SELECTION_COL_DEF,
            hideable: false
        },
        {
            field: 'id',
            headerName: 'WI ID',
            headerClassName: 'header',
            width: 150, minWidth: 150,
            sortable: selection,
            hideable: false,
            disableReorder: true,
            filterOperators: NumberFilterOperator,
            renderHeader: (params) => {
                return (
                    <Tooltip title="Work Item ID" arrow>
                        <div>{"WI ID"}</div>
                    </Tooltip>
                )
            }
        },
        {
            field: 'name',
            headerName: 'Work Item',
            flex: 1,
            minWidth: 200,
            sortable: selection,
            hideable: false,
            disableReorder: true,
            filterOperators: TextFilterOperator
        },        
        {
            field: 'child',
            headerName: 'Relationship',
            width: 200, minWidth: 120,
            sortable: selection, resizable: true,
            hideable: false,
            editable: false,
            hideSortIcons: false,
            filterOperators: ChildFilterOperator,
            renderCell: (params) => {
                const { value } = params;
                if (value) 
                    return (<>Child</>)
                else 
                    return (<>Parent</>)
            }
        },
        {
            field: 'parentWorkItems', headerName: 'Parent Work Item',
            minWidth: 120, width: 200,
            sortable: selection, resizable: true, hideable: true,
            headerClassName: 'header', hideSortIcons: false,
            filterOperators: ContainsOnlyTextFilterOperator,
            renderCell: (params) => {
                const parents = params.value;
                return (
                    <Grid container alignItems="center" sx={{
                        overflow: 'hidden',
                        whiteSpace: 'break-spaces',
                        lineHeight: '1.5em',
                        height: '3em'
                    }}
                    >
                        <Grid item xs={10}>
                            {parents?.length > 0 && <ParentWorkItemListTooltip
                                canAccessAllWorkItems={permissions?.canAccessAllWorkItems ?? false}
                                parent={parents ? parents : ''}
                                wave4 />}
                        </Grid>
                    </Grid>

                );
            }
        },
        {
            field: 'inProgressTask',
            headerName: 'In Progress Task',
            minWidth: 120, width: 200,
            sortable: selection,
            resizable: true, hideable: false,
            headerClassName: 'header', hideSortIcons: false, disableReorder: true,
            filterOperators: TextFilterOperator,
            renderCell: params => {
                const list = params.value as IListItem[];
                return (
                    <Grid container alignItems="center" sx={{
                        overflow: 'hidden',
                        whiteSpace: 'break-spaces',
                        lineHeight: '1.5em',
                        height: '3em'
                    }}
                    >
                        <Grid item xs={10}>
                            {list?.length > 0 && <TaskListTooltip list={list} />}
                        </Grid>
                    </Grid>

                );
            }
        },
        {
            field: 'contentType',
            headerName: 'Content Type',
            headerClassName: 'header',
            minWidth: 120, width: 150,
            sortable: selection, resizable: true,
            hideable: true,
            hideSortIcons: false,
            filterOperators: TextFilterOperator
        },
        {
            field: 'status',
            headerName: 'WI Status',
            minWidth: 150, width: 150,
            sortable: selection, resizable: true, hideable: true, hideSortIcons: false,
            headerClassName: 'header',
            filterOperators: StatusFilterOperatorWithoutCancelled,
            renderHeader: (params) => {
                return (
                    <Tooltip title="Work Item Status" arrow>
                        <div>{params.colDef.headerName}</div>
                    </Tooltip>
                )
            },
            renderCell: (params) => {
                const workItemStatus = params.value;
                return toPascalCase(workItemStatus);
            }
        },
        {
            field: 'template',
            headerName: 'Work Item Template',
            flex: 1,
            minWidth: 200,
            sortable: selection,
            hideable: false,
            disableReorder: true,
            filterOperators: TextFilterOperator
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Remove',
            headerClassName: 'header',
            getActions: (params) => ([
                <GridActionsCellItem
                    icon={<Ico_Delete />}
                    label="Remove"
                    onClick={() => {
                        apiRef?.current?.selectRow(params.id, false);
                    }}
                />
            ])
        }
    ];

    const columns = useMemo(() => {
        let newColumns = [];
        newColumns.push(...columnBase);
        if (selection) {
            const actionIndex = newColumns.findIndex(c => c.field === 'actions');
            if (actionIndex !== -1) {
                newColumns.splice(actionIndex, 1);
            }
        }
        return newColumns;
    }, [ selection, apiRef ]);
    
    const [ 
        columnsModel, 
        visibilityModel, 
        handleColumnVisibilityOrOrderChange 
    ] = useGridColumns({ columns: columns, settings, setSettings });

    return [
        columnsModel,
        visibilityModel,
        handleColumnVisibilityOrOrderChange
    ];
};

export default useAddRelatedColumns;